import Service from '@ember/service';
import Model from '@ember-data/model';
import { camelize } from '@ember/string';
import { getOwner } from '@ember/application';
import { setOwner } from '@ember/owner';
import { assert } from '@ember/debug';
import BaseAbility from 'uplisting-frontend/abilities/-base';
import { ABILITIES } from 'uplisting-frontend/abilities/registry';
import { singularize } from 'ember-inflector';
import type { ModelRegistry } from 'ember-data/types/registries/model';

const STOP_WORDS = ['in'];

interface INormalizedAbility {
  property: string;
  ability: string;
}

export default abstract class AbilitiesService<
  K extends keyof ModelRegistry,
> extends Service {
  public can(abilityString: string, model: typeof Model): boolean {
    assert('No ability string found', !!abilityString);
    assert('No ability model found', !!model);

    const { property, ability } = this.#normalizeAbilityString(abilityString);

    return this.#valueFor(property, ability, model);
  }

  #valueFor(property: string, ability: string, model: typeof Model): boolean {
    const AbilityClass = ABILITIES[ability];

    assert(`No ability found for 'ability:${ability}'`, !!AbilityClass);

    const owner = getOwner(this);

    assert('No owner found on abilities service', !!owner);

    const instance = AbilityClass.create({ model }) as BaseAbility<K>;
    setOwner(instance, owner);

    const result = instance.getAbility(property);

    instance.destroy();

    return result;
  }

  #normalizeAbilityString(string: string): INormalizedAbility {
    const parts = string.split(' ');
    const ability = singularize(parts.pop() as string);
    const last = parts[parts.length - 1];

    if (STOP_WORDS.includes(last as string)) {
      parts.pop();
    }

    const property = camelize(parts.join(' '));

    return { property, ability };
  }
}

declare module '@ember/service' {
  interface Registry {
    abilities: AbilitiesService<keyof ModelRegistry>;
  }
}
